import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import {
  createQuestionGroupOperatorLicenseTypesLicenseTypeIdQuestionGroupsPost,
  getQuestionGroupsOperatorLicenseTypesLicenseTypeIdQuestionGroupsGet,
  updateQuestionGroupOperatorQuestionGroupsGroupIdPatch,
} from "@/hera-api/client";
import { getLicenseTypesOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  LicenseTypeResponseSchema,
  QuestionType,
} from "@/hera-api/client/types.gen";
import { useListColumnFilterSync } from "@/hooks/use-sync-global-column-filters";
import { filterOperatorLicenseTypes } from "@/lib/license-type-utils";
import type { NamedIdOption } from "@/lib/sync-global-column-filters";
import { formatDateTime, getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RotateCcw, Trash2, Upload as UploadIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { QuestionGroupDetailsSheet } from "./question-group-details-sheet";
import { QuestionGroupUploadSheet } from "./question-group-upload-sheet";

interface QuestionGroup {
  id: string;
  name: string;
  license_type_id: string;
  created_at: string;
  last_updated_at: string;
  deleted_at: string | null;
}

// Define accessor keys
const QUESTION_GROUP_ACCESSOR_KEYS = [
  "name",
  "license_type",
  "status",
  "created_at",
  "actions",
] as const;

type QuestionGroupAccessorKeys = (typeof QUESTION_GROUP_ACCESSOR_KEYS)[number];
type QuestionGroupColumnProperties = ColumnProperties<
  QuestionGroup,
  QuestionGroupAccessorKeys
>;

interface QuestionGroupConfigurationPanelProps {
  /** When set, show only this question type (no nested tabs). When omitted, show nested tabs for all types. */
  questionType?: QuestionType;
  /**
   * When set, only load question groups for these license types (e.g. scoped by
   * jurisdiction + license type filters on Question Configuration). Omit for all types.
   */
  allowedLicenseTypeIds?: string[];
  selectedLicenseTypes?: string[];
  onSelectedLicenseTypesChange?: (ids: string[]) => void;
  licenseTypeCatalog?: readonly NamedIdOption[];
}

export function QuestionGroupConfigurationPanel({
  questionType: questionTypeProp,
  allowedLicenseTypeIds,
  selectedLicenseTypes = [],
  onSelectedLicenseTypesChange,
  licenseTypeCatalog = [],
}: QuestionGroupConfigurationPanelProps = {}) {
  const [internalTab, setInternalTab] = useState<QuestionType>("registration");
  const selectedTab = questionTypeProp ?? internalTab;
  const setSelectedTab = questionTypeProp != null ? () => {} : setInternalTab;
  const showNestedTabs = questionTypeProp == null;
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupLicenseTypeId, setNewGroupLicenseTypeId] = useState("");
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isUploadSheetOpen, setIsUploadSheetOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );
  useListColumnFilterSync({
    columnFilters,
    setColumnFilters,
    licenseTypeColumnId: "license_type",
    selectedLicenseTypes,
    setSelectedLicenseTypes: onSelectedLicenseTypesChange,
    licenseTypes: licenseTypeCatalog,
  });
  const [columnVisibility, setColumnVisibility] = useState<
    Record<QuestionGroupAccessorKeys, boolean>
  >(() => {
    const initialVisibility: Record<QuestionGroupAccessorKeys, boolean> =
      {} as Record<QuestionGroupAccessorKeys, boolean>;
    for (const key of QUESTION_GROUP_ACCESSOR_KEYS) {
      initialVisibility[key] = true; // All visible by default
    }
    return initialVisibility;
  });

  const queryClient = useQueryClient();

  // Fetch all license types
  const { data: licenseTypesData, isLoading: isLoadingLicenseTypes } = useQuery(
    {
      ...getLicenseTypesOptions(),
    },
  );

  const licenseTypes = useMemo(() => {
    const base =
      filterOperatorLicenseTypes(licenseTypesData?.license_types) ?? [];
    if (allowedLicenseTypeIds === undefined) {
      return base;
    }
    const allow = new Set(allowedLicenseTypeIds);
    return base.filter((lt) => allow.has(lt.id));
  }, [licenseTypesData?.license_types, allowedLicenseTypeIds]);

  const licenseTypeOptions = useMemo(() => {
    if (!licenseTypes.length) return [];
    return licenseTypes.map((lt: LicenseTypeResponseSchema) => ({
      label:
        lt.under_development === true
          ? `${lt.name} (Under Development)`
          : lt.name,
      value: lt.id,
    }));
  }, [licenseTypes]);

  // Fetch all question groups for the selected type.
  // Include license type ids in the key so we refetch when license types load (avoids showing empty list on refresh).
  const licenseTypeIds = useMemo(
    () =>
      licenseTypes
        .map((lt) => lt.id)
        .sort()
        .join(","),
    [licenseTypes],
  );
  const { data: allQuestionGroups, isLoading: isLoadingGroups } = useQuery({
    queryKey: [
      "questionGroupsByType",
      selectedTab,
      licenseTypeIds,
      allowedLicenseTypeIds?.join(",") ?? "all",
    ] as const,
    queryFn: async () => {
      if (!licenseTypes.length) return [];
      const allGroups: QuestionGroup[] = [];
      for (const lt of licenseTypes) {
        const response =
          await getQuestionGroupsOperatorLicenseTypesLicenseTypeIdQuestionGroupsGet(
            {
              path: { license_type_id: lt.id },
              query: { question_type: selectedTab },
            },
          );
        if (response.data) {
          allGroups.push(...response.data);
        }
      }
      return allGroups;
    },
    enabled: licenseTypes.length > 0,
  });

  const createMutation = useMutation({
    mutationFn: async ({
      licenseTypeId,
      name,
      question_type,
    }: {
      licenseTypeId: string;
      name: string;
      question_type: QuestionType;
    }) => {
      return await createQuestionGroupOperatorLicenseTypesLicenseTypeIdQuestionGroupsPost(
        {
          path: { license_type_id: licenseTypeId },
          body: { license_type_id: licenseTypeId, name, question_type },
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionGroupsByType"] });
      toast({
        title: "Success",
        description: "Question group created successfully",
      });
      setIsCreateSheetOpen(false);
      setNewGroupName("");
      setNewGroupLicenseTypeId("");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to create question group: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      groupId,
      name,
      deleted_at,
    }: { groupId: string; name: string; deleted_at: string | null }) => {
      return await updateQuestionGroupOperatorQuestionGroupsGroupIdPatch({
        path: { group_id: groupId },
        body: { name, deleted_at },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionGroupsByType"] });
      queryClient.invalidateQueries({ queryKey: ["questionGroups"] });
      toast({
        title: "Success",
        description: "Question group updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update question group: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    },
  });

  const handleCreate = (licenseTypeId: string, name: string) => {
    createMutation.mutate({ licenseTypeId, name, question_type: selectedTab });
  };

  const handleToggleActive = async (group: QuestionGroup) => {
    const deleted_at = group.deleted_at ? null : new Date().toISOString();
    updateMutation.mutate({
      groupId: group.id,
      name: group.name,
      deleted_at,
    });
  };

  const handleRowClick = useCallback((groupId: string) => {
    setSelectedGroupId(groupId);
    setIsEditSheetOpen(true);
  }, []);

  const handleCreateSheetOpenChange = (open: boolean) => {
    if (!open) {
      setNewGroupName("");
      setNewGroupLicenseTypeId("");
    }
    setIsCreateSheetOpen(open);
  };

  const currentGroups = allQuestionGroups ?? [];
  const isLoading = isLoadingLicenseTypes || isLoadingGroups;
  const noLicenseTypesForFilters =
    allowedLicenseTypeIds !== undefined &&
    !isLoadingLicenseTypes &&
    licenseTypes.length === 0;

  const getLicenseTypeName = (licenseTypeId: string) => {
    const lt = licenseTypes?.find(
      (t: LicenseTypeResponseSchema) => t.id === licenseTypeId,
    );
    return lt?.name || "Unknown";
  };

  // Column properties function
  const getQuestionGroupColumnProperties = (
    accessorKey: QuestionGroupAccessorKeys,
  ): QuestionGroupColumnProperties => {
    switch (accessorKey) {
      case "name":
        return {
          displayName: "Group Name",
          defaultVisible: true,
          allowBlankFilter: false,
          getValue: (row: QuestionGroup) => row.name,
        };
      case "license_type":
        return {
          displayName: "License Type",
          defaultVisible: true,
          allowBlankFilter: false,
          getValue: (row: QuestionGroup) =>
            getLicenseTypeName(row.license_type_id),
        };
      case "status":
        return {
          displayName: "Status",
          defaultVisible: true,
          allowBlankFilter: false,
          getValue: (row: QuestionGroup) =>
            row.deleted_at ? "Inactive" : "Active",
        };
      case "created_at":
        return {
          displayName: "Created",
          defaultVisible: true,
          allowBlankFilter: false,
          getValue: (row: QuestionGroup) => formatDateTime(row.created_at),
        };
      case "actions":
        return {
          displayName: "Actions",
          defaultVisible: true,
          filterable: false,
          getValue: () => "",
          renderCell: (row: QuestionGroup) => {
            const isActive = row.deleted_at === null;
            return (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleActive(row);
                  }}
                  disabled={updateMutation.isPending}
                  className="flex-1 sm:flex-none min-w-[100px]"
                >
                  {isActive ? (
                    <>
                      <Trash2 className="h-4 w-4" />
                      <span className="ml-1">Deactivate</span>
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4" />
                      <span className="ml-1">Reactivate</span>
                    </>
                  )}
                </Button>
              </div>
            );
          },
        };
      default:
        return {
          displayName: accessorKey,
          defaultVisible: true,
          getValue: () => "",
        };
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 min-w-0">
      <div className="flex shrink-0 justify-between items-center">
        {showNestedTabs ? (
          <div className="flex gap-2 border-b">
            <button
              type="button"
              onClick={() => setSelectedTab("registration")}
              className={`px-4 py-2 font-medium transition-colors ${
                selectedTab === "registration"
                  ? "border-b-2 border-primary text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Registration
            </button>
            <button
              type="button"
              onClick={() => setSelectedTab("inspection")}
              className={`px-4 py-2 font-medium transition-colors ${
                selectedTab === "inspection"
                  ? "border-b-2 border-primary text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Inspection
            </button>
            <button
              type="button"
              onClick={() => setSelectedTab("deregistration")}
              className={`px-4 py-2 font-medium transition-colors ${
                selectedTab === "deregistration"
                  ? "border-b-2 border-primary text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              De-registration
            </button>
          </div>
        ) : (
          <div />
        )}
        <div className="flex flex-wrap gap-2 justify-end">
          <Button type="button" onClick={() => setIsUploadSheetOpen(true)}>
            <UploadIcon className="h-4 w-4 mr-2" />
            Upload groups
          </Button>
          <Button
            type="button"
            onClick={() => {
              setNewGroupName("");
              setNewGroupLicenseTypeId("");
              setIsCreateSheetOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Question Group
          </Button>
        </div>
      </div>

      <Sheet
        open={isCreateSheetOpen}
        onOpenChange={handleCreateSheetOpenChange}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-md flex flex-col gap-0"
        >
          <SheetHeader className="space-y-1">
            <SheetTitle>Add New Question Group</SheetTitle>
            <SheetDescription>
              Choose a license type and name for this{" "}
              {selectedTab === "registration"
                ? "registration"
                : selectedTab === "inspection"
                  ? "inspection"
                  : "de-registration"}{" "}
              question group.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-4 px-4 pb-4 pt-2 overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="new-group-license-type-sheet">License Type</Label>
              <Select
                options={licenseTypeOptions}
                value={newGroupLicenseTypeId ? [newGroupLicenseTypeId] : []}
                onValueChange={(values) =>
                  setNewGroupLicenseTypeId(values[0] || "")
                }
                placeholder="Select license type"
                searchPlaceholder="Search license types..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-group-name-sheet">Group Name</Label>
              <Input
                id="new-group-name-sheet"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Enter group name"
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    newGroupName.trim() &&
                    newGroupLicenseTypeId &&
                    !createMutation.isPending
                  ) {
                    e.preventDefault();
                    handleCreate(newGroupLicenseTypeId, newGroupName.trim());
                  }
                }}
              />
            </div>
          </div>
          <SheetFooter className="flex-row gap-2 sm:justify-between border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleCreateSheetOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() =>
                handleCreate(newGroupLicenseTypeId, newGroupName.trim())
              }
              disabled={
                !newGroupName.trim() ||
                !newGroupLicenseTypeId ||
                createMutation.isPending
              }
            >
              {createMutation.isPending ? "Creating..." : "Create group"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {isLoading ? (
        <div className="shrink-0 text-center py-8 text-muted-foreground">
          Loading question groups...
        </div>
      ) : noLicenseTypesForFilters ? (
        <div className="shrink-0 text-center py-8 text-muted-foreground">
          No license types apply to the selected jurisdictions with the current
          license type filter. Adjust or clear filters to see question groups.
        </div>
      ) : currentGroups.length === 0 ? (
        <div className="shrink-0 text-center py-8 text-muted-foreground">
          No question groups found. Use &quot;Add New Question Group&quot; to
          add one.
        </div>
      ) : (
        <DashboardTable<QuestionGroup, QuestionGroupAccessorKeys>
          data={currentGroups}
          onRowClick={handleRowClick}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          accessorKeys={QUESTION_GROUP_ACCESSOR_KEYS}
          getColumnProperties={getQuestionGroupColumnProperties}
          getRowId={(row) => row.id}
          tablePersistenceKey="non-registrant-question-groups"
          initialSortColumn="created_at"
          initialSortDirection="desc"
        />
      )}

      <QuestionGroupDetailsSheet
        isOpen={isEditSheetOpen}
        onOpenChange={setIsEditSheetOpen}
        groupId={selectedGroupId}
        groupType={selectedTab}
      />

      <QuestionGroupUploadSheet
        isOpen={isUploadSheetOpen}
        onOpenChange={setIsUploadSheetOpen}
        questionType={selectedTab}
      />
    </div>
  );
}
